import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useCustomNavigation } from "../../utility/Utility.ts";
import { NavbarElementProps, NavbarItemComponentProps } from "../../constants/interface.ts";

const NavbarItemComponent = (props: NavbarItemComponentProps) => {
    const {index, element} = props;
    const {t} = useTranslation();
    const customNavigate = useCustomNavigation();
    const [navbarItemIndex, setNavbarItemIndex] = useState<number>(-1); // Default is -1 (no item open)
    const [navbarSubItem, setNavbarSubItem] = useState<null | NavbarElementProps>(null);
    const [navbarSubItemIndex, setNavbarSubItemIndex] = useState<number>(-1); // Default is -1 (no sub-item open)
    const animationProps = {
        initial: { opacity: 0, y: -10 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -10, transition: { duration: 0.3, ease: "easeInOut" } },
        transition: { duration: 0.5, ease: "easeInOut" }
    };

    const handleNavigation = (isRouterLink: boolean, link: string) => {
        customNavigate(isRouterLink, link);
        resetAll(); // reseting on navigation to the new page.
    };
    
    const handleShowNavbarSubItem = (index: number, item: NavbarElementProps | null) => {
        setNavbarSubItem(null); // initially set to null to properly showcase the animation

        /* Here, we are using a timeout to show a smooth animation of 'subItems' getting added to the UI. */
        setTimeout(() => {
            setNavbarSubItem(item || null);
        }, (navbarSubItemIndex!==-1) ? 300 : 0); // If the sub-item is already open, wait for 500ms to showcase the 'exit' animation (previous sub-item) before showing the new sub-item.
        setNavbarSubItemIndex(index);
    };

    const resetAll = () => {
        setNavbarSubItem(null);
        setNavbarItemIndex(-1);
        setNavbarSubItemIndex(-1);
    };

    return (
        <div className="grid items-center"
            onMouseEnter={() => setNavbarItemIndex(index)} // To show the dropdown on mouse enter
            onMouseLeave={() => setNavbarItemIndex(-1)}    // To hide the dropdown on mouse leave
        >
            <div className={`relative grid cursor-pointer h-full items-center`}>
                <div className="relative z-[2] flex gap-2">
                    <p className="text-white text-base font-semibold">{t(element.title)}</p>
                    <img loading="lazy" src="/assets/images/svg/chevron-down-icon.svg" alt="" />
                </div>
                
                {/* Dropdown Content (with Smooth Animation) */}
                <AnimatePresence>
                    {navbarItemIndex === index && (
                        <motion.div
                            {...animationProps}
                            className="absolute left-0 top-full pt-10 max-h-[90vh] overflow-auto hide-scrollbar cursor-default"
                        >
                            <div className="flex flex-wrap bg-white shadow-lg z-10 w-max rounded-xl"
                            >
                                <ul className="p-2 w-64 xl:w-80">
                                    {
                                        (element.list) && 
                                        element.list.map((item, subIndex: number) => (
                                            <a key={subIndex}
                                                className={`flex items-center gap-2 p-3 cursor-pointer rounded-lg hover:bg-light-gray-F5F ${ (subIndex === navbarSubItemIndex) && 'bg-light-gray-F5F'} text-black-1A1 ${item.link && 'hover:text-blue-700'}`}
                                                href={item.link || ''}
                                                style={{ ...((item.link === 'no-data') && {cursor: "not-allowed", opacity: 0.5})}} //disabling for the 'no data', remove the code later
                                                onClick={(e) => {
                                                    e.preventDefault(); // to previde the "Open link in new tab" option, on mouse right click
                                                    item.link && handleNavigation(true, item.link);
                                                }} /* To navigate to the link */
                                                onMouseEnter={() => handleShowNavbarSubItem(subIndex, ((item.list) ? item : null))} /* To show the sub items when mouse enters the parent */
                                                >
                                                <img loading="lazy" src={item.img} alt={t(item.title)}
                                                    className="self-baseline h-6"
                                                />
                                                <div className="flex flex-col gap-1">
                                                    <p className={``}>{t(item.title)}</p>
                                                    <>
                                                    {
                                                        (item.para) &&
                                                        <p className="text-sm font-normal text-gray-717">{t(item.para)}</p>
                                                    }
                                                    </>
                                                </div>
                                            </a>
                                        ))
                                    }
                                </ul>
                                
                                {/* Sub Items */}
                                <AnimatePresence>
                                    {navbarSubItem && (
                                        <motion.div
                                            {...animationProps}
                                            className={`relative p-5 w-[32rem] xl:w-[35rem]`}
                                        >
                                            {
                                                (navbarSubItem?.list?.[0]?.img) && (
                                                    <div className="pt-5 pr-5 navbar-sub-item-image-container h-[21.5rem]">
                                                        <img
                                                            src={navbarSubItem.list[0].img} loading="lazy" alt=""
                                                            className="rounded-md object-cover object-center w-full h-full max-h-[21.5rem]"
                                                        />
                                                    </div>
                                                )
                                            }
                                            <div className={`navbar-sub-item-container`}>
                                                {
                                                    navbarSubItem?.list?.map((submItem, subIndex: number) => (
                                                        <div key={subIndex} className="flex flex-col gap-3 mb-5"
                                                            style={{breakInside: 'avoid'}}
                                                            >
                                                            <p className="text-sm font-semibold text-black-1A1">{t(submItem.title)}</p>
                                                            <div>
                                                                {
                                                                    submItem?.list?.map((listItem, listItemIndex: number) => (
                                                                        <div key={listItemIndex} 
                                                                        >
                                                                            <a href={listItem.link || ''}
                                                                                className="w-fit text-sm font-normal text-gray-717 cursor-pointer hover:text-blue-700"
                                                                                style={{ ...((listItem.link === 'no-data') && {cursor: "not-allowed", opacity: 0.5})}} //disabling for the 'no data', remove the code later
                                                                                onClick={(e) => {
                                                                                    e.preventDefault(); // to previde the "Open link in new tab" option, on mouse right click
                                                                                    (listItem.link) && handleNavigation(true, listItem.link);
                                                                                }}
                                                                                >
                                                                                <p className="font-medium">{t(listItem.title)} </p>
                                                                                <>
                                                                                    {
                                                                                        (listItem.para) &&
                                                                                        <p className="italic text-xs">{t(listItem.para)}</p>
                                                                                    }
                                                                                </>
                                                                            </a>
                                                                        </div>
                                                                    ))
                                                                }
                                                            </div>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </div>
    );
};

export default NavbarItemComponent;